// The builder's operations (spec/dashboard.md section 7.1): every action stages workspace changes at once — a new node is a
// draft with a temp id, a changed node an edit, a removed node a staged deletion or a dropped draft — so no orphan node
// ever survives and the Changes panel, undo and Save All see one consistent graph.
import type { Document, Json } from '../../api/types';
import { entityKey, isTempId, type Workspace } from '../../workspace/Workspace';
import type { ContentCatalog } from '../ContentContext';
import type { DocumentLike } from '../format';
import { createDocumentDraft } from '../widgets/context';
import { CONDITION_NS, kindOf, type ConditionKind } from './model';

export interface TreeEnv {
  workspace: Workspace;
  catalog: ContentCatalog;
  /** condition templates the picker does not offer in this context (an override group offers no ABTestCondition, section 8.3) */
  exclude?: readonly string[];
}

/** Where a node hangs: the owner's parameter (the root) or a parent node's field. */
export type Slot = { kind: 'owner'; set: (id: string | null) => void } | { kind: 'child'; parentId: string; field: 'conditions' | 'condition' | 'filter'; index?: number };

export function nodeOf(catalog: ContentCatalog, id: string | null | undefined): DocumentLike | undefined {
  return id ? catalog.resolve(id) : undefined;
}

function dataOf(node: DocumentLike | undefined): Record<string, Json> {
  return (node?.data ?? {}) as Record<string, Json>;
}

/** The ids a node references as children: the logic list, Not's one, a list leaf's filter and condition. */
export function childIds(node: DocumentLike | undefined): string[] {
  const data = dataOf(node);
  const out: string[] = [];
  if (Array.isArray(data.conditions)) for (const c of data.conditions) if (typeof c === 'string') out.push(c);
  for (const field of ['condition', 'filter'] as const) if (typeof data[field] === 'string') out.push(data[field] as string);
  return out;
}

/** Every id reachable from the root, the root first; `missing` lists the ids the resolver has not answered yet. */
export function collectTree(catalog: ContentCatalog, rootId: string | null): { ids: string[]; missing: string[] } {
  const ids: string[] = [];
  const missing: string[] = [];
  const seen = new Set<string>();
  const walk = (id: string, depth: number) => {
    if (seen.has(id) || depth > 32) return;
    seen.add(id);
    ids.push(id);
    const node = nodeOf(catalog, id);
    if (!node) {
      missing.push(id);
      return;
    }
    for (const c of childIds(node)) walk(c, depth + 1);
  };
  if (rootId) walk(rootId, 0);
  return { ids, missing };
}

function baseOf(catalog: ContentCatalog, id: string): { version: number | null; value: Json } | undefined {
  if (isTempId(id)) return undefined;
  const server = catalog.resolver.get(id) as (DocumentLike & { version?: number | string }) | undefined;
  return server ? { version: Number(server.version ?? 1), value: server as unknown as Json } : undefined;
}

function metaOf(catalog: ContentCatalog, node: DocumentLike | undefined, id: string) {
  const template = node?.template ?? '';
  return { label: node ? catalog.label(node) : id, template };
}

/** A new node of a kind (or any condition template) staged as a draft; returns its temp id. */
export function createNode(env: TreeEnv, kindOrTemplate: ConditionKind | string, data: Record<string, Json> = {}): string {
  const template = typeof kindOrTemplate === 'string' ? kindOrTemplate : kindOrTemplate.template;
  const kind = typeof kindOrTemplate === 'string' ? kindOf(kindOrTemplate) : kindOrTemplate;
  const def = env.catalog.templates.get(template);
  if (!def) throw new Error(`No condition template ${template} on this branch`);
  return createDocumentDraft(env.workspace, def, { ...(kind?.defaults ?? {}), ...data });
}

/** One field of a node. */
export function editNode(env: TreeEnv, id: string, field: string, value: Json): void {
  const node = nodeOf(env.catalog, id);
  env.workspace.edit('document', id, `data.${field}`, value, { base: baseOf(env.catalog, id), ...metaOf(env.catalog, node, id) });
}

/** Points a slot at a node (or clears it). */
export function setSlot(env: TreeEnv, slot: Slot, id: string | null): void {
  if (slot.kind === 'owner') {
    slot.set(id);
    return;
  }
  const parent = nodeOf(env.catalog, slot.parentId);
  if (slot.field === 'conditions') {
    const list = [...((dataOf(parent).conditions as Json[] | undefined) ?? [])];
    if (slot.index !== undefined && slot.index < list.length) {
      if (id === null) list.splice(slot.index, 1);
      else list[slot.index] = id;
    } else if (id !== null) list.push(id);
    editNode(env, slot.parentId, 'conditions', list);
  } else {
    editNode(env, slot.parentId, slot.field, id);
  }
}

/** Removes a node and its subtree: a draft is dropped, a server document is staged for deletion. */
export function removeSubtree(env: TreeEnv, id: string): void {
  const node = nodeOf(env.catalog, id);
  for (const c of childIds(node)) removeSubtree(env, c);
  removeOne(env, id);
}

function removeOne(env: TreeEnv, id: string): void {
  const node = nodeOf(env.catalog, id);
  if (isTempId(id)) {
    if (env.workspace.getDraft(entityKey('document', id))) env.workspace.remove('document', id);
    return;
  }
  env.workspace.remove('document', id, baseOf(env.catalog, id), metaOf(env.catalog, node, id));
}

/** Detaches a node from its slot and removes its subtree. */
export function deleteNode(env: TreeEnv, slot: Slot, id: string): void {
  setSlot(env, slot, null);
  removeSubtree(env, id);
}

/** Adds a new node of a kind into a slot (a list slot appends). */
export function addNode(env: TreeEnv, slot: Slot, kindOrTemplate: ConditionKind | string, data: Record<string, Json> = {}): string {
  const id = createNode(env, kindOrTemplate, data);
  setSlot(env, slot, id);
  return id;
}

/**
 * Replaces a node by a new one of another template in the same slot, carrying the given data (the And ↔ Or toggle keeps the
 * children, the ∈ swap keeps the path); the old node alone is removed — its children now belong to the new one.
 */
export function replaceNode(env: TreeEnv, slot: Slot, oldId: string, template: string, data: Record<string, Json>): string {
  const created = createNode(env, template, data);
  setSlot(env, slot, created);
  removeOne(env, oldId);
  return created;
}

/** And ↔ Or: the other logic node with the same children. */
export function toggleLogic(env: TreeEnv, slot: Slot, id: string): string | null {
  const node = nodeOf(env.catalog, id);
  if (!node) return null;
  const other = node.template === `${CONDITION_NS}And` ? `${CONDITION_NS}Or` : node.template === `${CONDITION_NS}Or` ? `${CONDITION_NS}And` : null;
  if (!other) return null;
  return replaceNode(env, slot, id, other, { conditions: [...((dataOf(node).conditions as Json[] | undefined) ?? [])] });
}

/** The root an owner gets when its first node is added: an And wrapping nothing yet. */
export function ensureRoot(env: TreeEnv, owner: Extract<Slot, { kind: 'owner' }>, current: string | null): string {
  if (current && nodeOf(env.catalog, current)) return current;
  const id = createNode(env, `${CONDITION_NS}And`);
  owner.set(id);
  return id;
}

/** The server document behind a node, for bases (the resolver's copy, a DocumentDto). */
export function serverNode(catalog: ContentCatalog, id: string): Document | undefined {
  return isTempId(id) ? undefined : (catalog.resolver.get(id) as Document | undefined);
}
